import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Phone, MapPin } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { CITIES, SERVICES, BUSINESS, IMAGES } from "@/siteData";
import { trackEvent } from "@/lib/analytics";
import NotFound from "@/pages/NotFound";

const LocationPage = () => {
  const { slug } = useParams();
  const city = CITIES.find((c) => c.slug === slug);
  if (!city) return <NotFound />;

  const others = CITIES.filter((c) => c.slug !== slug);

  return (
    <>
      <Seo title={city.metaTitle} description={city.metaDescription} />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-12" data-testid={`location-page-${slug}`}>
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full bg-amber-600/10 px-4 py-1.5 font-mono-accent text-xs uppercase tracking-[0.25em] text-amber-700">
                <MapPin className="h-3.5 w-3.5" />
                {city.city}, {city.state}
              </span>
              <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl">
                {city.headline}
              </h1>
              {city.body.map((p, i) => (
                <p key={i} className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600">
                  {p}
                </p>
              ))}
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid={`location-${slug}-estimate-button`}
                  className="inline-flex items-center gap-2 rounded-full bg-amber-600 px-7 py-3.5 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:scale-105 hover:bg-amber-700"
                >
                  Get a Free Estimate in {city.city}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={BUSINESS.phoneHref}
                  data-testid={`location-${slug}-call-button`}
                  onClick={() => trackEvent("phone_click", { location: `location-${slug}` })}
                  className="inline-flex items-center gap-2 rounded-full border-2 border-slate-900 px-7 py-3 text-sm font-semibold text-slate-900 transition-colors duration-200 hover:bg-slate-900 hover:text-white"
                >
                  <Phone className="h-4 w-4" />
                  {BUSINESS.phone}
                </a>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="relative">
                <div className="absolute -left-4 -top-4 h-full w-full rounded-[2rem] border-2 border-amber-600/40" />
                <img
                  src={IMAGES.exterior3}
                  alt={`Exterior painting project by JC Colors Remodeling in ${city.city}, ${city.state}`}
                  loading="eager"
                  className="relative h-80 w-full rounded-[2rem] object-cover shadow-2xl shadow-slate-900/20 sm:h-[420px]"
                />
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal className="mt-20">
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Our services in {city.city}, {city.state}
          </h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={`/${s.slug}`}
                data-testid={`location-${slug}-service-${s.slug}`}
                className="group flex h-full flex-col justify-between rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-amber-600/50 hover:shadow-lg"
              >
                <div>
                  <h3 className="font-display text-lg font-bold text-slate-900">
                    {s.title} in {city.city}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.short}</p>
                </div>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-700">
                  Learn more
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            ))}
          </div>
        </Reveal>

        <Reveal className="mt-16">
          <div className="rounded-3xl border border-slate-200 bg-white p-8 sm:p-10">
            <h2 className="font-display text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
              We also serve
            </h2>
            <div className="mt-5 flex flex-wrap gap-3">
              {others.map((c) => (
                <Link
                  key={c.slug}
                  to={`/service-areas/${c.slug}`}
                  data-testid={`location-${slug}-nearby-${c.slug}`}
                  className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 transition-colors duration-200 hover:border-amber-600 hover:text-amber-700"
                >
                  {c.city}, {c.state}
                </Link>
              ))}
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
};

export default LocationPage;
